import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Camera, X } from "lucide-react";

const HINTS = {
  face: "Ubica tu rostro de frente, bien iluminado y solo tú en la imagen.",
  document: "Coloca el documento completo dentro del recuadro, con buena luz y sin reflejos.",
};

export default function CameraModal({ mode = "face", onCapture, onClose }) {
  const videoRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let stream = null;
    navigator.mediaDevices.getUserMedia({
      video: { facingMode: mode === "face" ? "user" : "environment" },
      audio: false,
    })
      .then((s) => {
        if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; }
        stream = s;
        if (videoRef.current) videoRef.current.srcObject = s;
      })
      .catch(() => setError("No pudimos acceder a la cámara. Revisa los permisos del navegador."));
    return () => {
      cancelled = true;
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [mode]);

  const capture = () => {
    const video = videoRef.current;
    if (!video?.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (blob) onCapture(new File([blob], `${mode}.jpg`, { type: "image/jpeg" }));
    }, "image/jpeg", 0.92);
  };

  const isFace = mode === "face";

  return createPortal(
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[10030] p-4">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b">
          <p className="font-bold text-gray-800">{isFace ? "Tomar foto de perfil" : "Fotografiar documento"}</p>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>

        <div className="relative bg-black aspect-square flex items-center justify-center">
          {error ? (
            <p className="text-white text-sm p-6 text-center">{error}</p>
          ) : (
            <>
              <video ref={videoRef} autoPlay playsInline muted
                className={`w-full h-full object-cover ${isFace ? "-scale-x-100" : ""}`} />
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                {isFace ? (
                  <div className="w-[60%] aspect-[3/4] rounded-[50%] border-2 border-dashed border-white/80" />
                ) : (
                  <div className="w-[88%] aspect-[1.586] rounded-xl border-2 border-dashed border-amber-300 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]">
                    <span className="absolute -top-6 left-0 right-0 text-center text-xs text-white/90">Recuadro del documento</span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="p-4 space-y-2">
          <p className="text-xs text-gray-500 text-center">{HINTS[mode]}</p>
          <button type="button" onClick={capture} disabled={!!error}
            className="w-full py-3 rounded-xl bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white font-semibold flex items-center justify-center gap-2">
            <Camera size={16} /> Capturar
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
